import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Download, RefreshCw, Search, History, Loader2, X, CalendarRange } from "lucide-react";
import { toast } from "sonner";

// Full-screen Edit History viewer — same data as the in-panel modal, just
// rendered in its own tab so the user can scan a long audit log comfortably.
const REVERT_PASSWORD = "Esr@88";

const DatabaseAHistoryPage = () => {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [track, setTrack] = useState(params.get("track_no") || "");
  const [from, setFrom] = useState(params.get("date_from") || "");
  const [to, setTo] = useState(params.get("date_to") || "");

  const buildParams = useCallback((extra = {}) => {
    const p = new URLSearchParams();
    if (track) p.set("track_no", track);
    if (from) p.set("date_from", from);
    if (to) p.set("date_to", to);
    Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    return p;
  }, [track, from, to]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/database-a/history?${buildParams({ limit: 500 })}`,
        { headers: getAuthHeader() }
      );
      setItems(res.data.items || []);
    } catch {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  // Load on mount only — filter changes trigger fetchHistory via the Apply button.
  // Intentional empty-deps; we don't want to re-fetch on every keystroke.
  // (no eslint-disable comment to avoid breaking projects without that rule)
  // eslint-disable-next-line
  useEffect(() => { fetchHistory(); }, []);

  const downloadExcel = async () => {
    try {
      const res = await axios.get(
        `${API}/database-a/history/excel?${buildParams()}`,
        { headers: getAuthHeader(), responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const suffix = (from || to) ? `_${from || "start"}_to_${to || "today"}` : "";
      a.download = `database_a_history${suffix}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 800);
      toast.success("Downloaded");
    } catch {
      toast.error("Excel download failed");
    }
  };

  const revert = async (h) => {
    const pw = window.prompt(`Revert ${h.track_no} • ${h.field} back to "${h.old_value ?? ""}"?\n\nEnter team password to confirm:`);
    if (pw === null) return;
    if (pw !== REVERT_PASSWORD) { toast.error("Wrong password — revert cancelled"); return; }
    try {
      await axios.post(`${API}/database-a/history/${h.id}/revert`, {}, { headers: getAuthHeader() });
      toast.success("Reverted — change logged");
      fetchHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revert failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dba-history-page">
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-indigo-600" />
            <div>
              <div className="text-sm font-semibold">Database A — Edit History</div>
              <div className="text-[11px] text-slate-500">Full-screen audit trail · {items.length} entries shown</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 text-xs">
              <CalendarRange className="w-3.5 h-3.5 text-slate-400" />
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border rounded px-1 py-0.5 text-xs" />
              <span className="text-slate-400">→</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border rounded px-1 py-0.5 text-xs" />
              {(from || to) && (
                <button onClick={() => { setFrom(""); setTo(""); setTimeout(fetchHistory, 0); }} title="Clear" className="text-slate-400 hover:text-slate-600">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <input
                value={track}
                onChange={(e) => setTrack(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchHistory()}
                placeholder="Filter by tracking…"
                className="pl-7 pr-2 py-1 text-xs border rounded w-44 focus:outline-none focus:ring-1 focus:ring-indigo-400"
              />
            </div>
            <Button size="sm" variant="outline" onClick={fetchHistory} className="h-8 px-3 text-xs">
              <RefreshCw className="w-3 h-3 mr-1" /> Apply
            </Button>
            <Button size="sm" onClick={downloadExcel} className="h-8 px-3 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">
              <Download className="w-3 h-3 mr-1" /> Excel
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        <div className="bg-white border rounded-lg shadow-sm overflow-auto">
          {loading ? (
            <div className="py-16 flex items-center justify-center text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-slate-500 text-sm">
              No entries match the current filters.
            </div>
          ) : (
            <table className="w-full text-xs border-collapse" data-testid="dba-history-page-table">
              <thead className="bg-slate-100 text-slate-600 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 border-b">When</th>
                  <th className="text-left px-3 py-2 border-b">By</th>
                  <th className="text-left px-3 py-2 border-b">Tracking</th>
                  <th className="text-left px-3 py-2 border-b">Field</th>
                  <th className="text-left px-3 py-2 border-b">Old → New</th>
                  <th className="text-left px-3 py-2 border-b">Invoice</th>
                  <th className="text-left px-3 py-2 border-b w-20"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((h) => (
                  <tr key={h.id} className="hover:bg-indigo-50/40">
                    <td className="px-3 py-1.5 border-b whitespace-nowrap text-slate-500">
                      {(() => { try { return new Date(h.ts).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return h.ts; } })()}
                    </td>
                    <td className="px-3 py-1.5 border-b whitespace-nowrap">
                      <div className="font-medium text-slate-800">{h.user_name || h.user_email}</div>
                      {h.user_name && h.user_email && <div className="text-[10px] text-slate-400">{h.user_email}</div>}
                    </td>
                    <td className="px-3 py-1.5 border-b font-mono text-slate-700">{h.track_no}</td>
                    <td className="px-3 py-1.5 border-b text-slate-700">
                      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px] uppercase tracking-wide">{h.field}</span>
                      {h.reverted_from && <span className="ml-1 px-1 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] uppercase">revert</span>}
                    </td>
                    <td className="px-3 py-1.5 border-b">
                      <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-700 line-through mr-1">{String(h.old_value ?? "") || "∅"}</span>
                      →
                      <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-medium">{String(h.new_value ?? "") || "∅"}</span>
                    </td>
                    <td className="px-3 py-1.5 border-b text-slate-600">{h.invoice_no || "-"}</td>
                    <td className="px-3 py-1.5 border-b text-right">
                      <button
                        onClick={() => revert(h)}
                        className="px-2 py-0.5 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-[10px] font-medium"
                      >
                        ↩ Revert
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <p className="text-[11px] text-slate-500 mt-3 italic">
          Reverting a row is password-protected. Each revert is logged as a new entry so the audit trail stays complete.
        </p>
      </div>
    </div>
  );
};

export default DatabaseAHistoryPage;
